import React, { useState } from "react";
import styles from "../../Application.module.scss";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import useCommonStore from "../../../../stores/common";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import {
  maskNumber,
  formatPhoneNumber,
} from "../../../../../components/helper/helper";
import CrossIconRed from "../../../../assets/svg/icons/common/CrossIconRed";
import CheckMarkGreen from "../../../../assets/svg/icons/common/CheckMarkGreen";
import CaretRight24 from "../../../../assets/svg/icons/common/CaretRight24";

function BusinessInformation({ appData }) {
  const expandAppDetails = useCommonStore((state) => state.expandDetails);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery(768);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const optionsPremiseType = [
    { value: 1, label: "Commercial/Industrial" },
    { value: 2, label: "Kiosk" },
    { value: 3, label: "Mailbox" },
    { value: 4, label: "Office Building" },
    { value: 5, label: "Residential/Home Office" },
    { value: 6, label: "Retail Store Front" },
    { value: 7, label: "Mobile/On-the-Go" },
    { value: 8, label: "Website" },
  ];

  const getPremiseType = (id) => {
    let premise = "";
    optionsPremiseType.forEach(function (item) {
      if (item.value == id) {
        premise = item.label;
      }
    });
    return premise;
  };

  const renderYesNo = (value) => {
    if (value !== "" && value !== undefined && value !== null) {
      return value === "yes" ? (
        <>
          <CheckMarkGreen /> Yes
        </>
      ) : (
        <>
          <CrossIconRed /> No
        </>
      );
    } else {
      return "-";
    }
  };

  const renderSection = (title, items) => {
    return (
      <div className={styles.info_section}>
        {title && (
          <>
            <div className={styles.section_title}>
              <h5>{title}</h5>
            </div>
            <div className={styles.desktop_heading}>
              <HeadingWithLine text={title} />
            </div>
          </>
        )}
        <div className={styles.info_grid}>
          {items.map((item, index) => (
            <div key={index} className={styles.info_item}>
              <span className={styles.label}>{item.label}</span>
              <span className={styles.value}>{item.value}</span>
            </div>
          ))}
        </div>
        <ul className={styles.desktop_list}>
          {items.map((item, index) => (
            <li key={index}>
              <div>
                <p>{item.label}</p>
                <p>{item.value}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const hasPaylianceProcessor =
    appData?.override_bank?.some((bank) => bank.label === "Payliance") ?? false;

  const businessInfoItems = [
    { label: "Type of Ownership", value: appData?.field_ownership || "-" },
    { label: "Legal Business Name", value: appData?.field_legalname || "-" },
    { label: "DBA Name", value: appData?.field_dba || "-" },
    { label: "Tax ID / EIN", value: appData?.field_ein ? maskNumber(appData?.field_ein) : "-" },
    { label: "Premise Type", value: getPremiseType(appData?.premise_type) || "-" },
    { 
      label: "Period of Ownership", 
      value: `${appData?.field_ownershipyears || "-"} year(s) ${appData?.field_ownershipmonths || "-"} month(s)`
    },
    { 
      label: "Business Type", 
      value: appData?.field_businesstype 
        ? appData?.field_businesstype
        : appData?.powerappbusinessprofile?.product_service_sold || "—"
    },
    { 
      label: "Industry Type (MCC)", 
      value: appData?.mcc_info 
        ? `${appData?.mcc_info?.description} (${appData?.mcc_info?.mcc})`
        : "—"
    },
    ...(hasPaylianceProcessor
      ? [
          {
            label: "Payliance Industry",
            value: appData?.powerappbusinessprofile?.payliance_industry || "-",
          },
        ]
      : []),
    { label: "Tax exempt", value: renderYesNo(appData?.powerappbusinessprofile?.tax_exempt) },
  ];

  const contactsItems = [
    { 
      label: "Business Phone Number", 
      value: appData?.field_dbaphone ? formatPhoneNumber(appData?.field_dbaphone) : "-"
    },
    { label: "Business Email", value: appData?.field_businessemail || "-" },
    { label: "Business Website URL", value: appData?.field_website || "-" },
  ];

  const dbaAddressItems = [
    { label: "DBA Street", value: appData?.field_dbastreet || "-" },
    { label: "DBA City", value: appData?.field_dbacity || "-" },
    { label: "DBA State", value: appData?.field_dbastate || "-" },
    { label: "DBA Zip", value: appData?.field_dbazip || "-" },
    { 
      label: "Period of Location", 
      value: `${appData?.field_locationyears || "-"} year(s) ${appData?.field_locationmonths || "-"} month(s)`
    },
  ];

  const legalAddressItems = [
    { label: "Street", value: appData?.field_legalstreet || "-" },
    { label: "City", value: appData?.field_legalcity || "-" },
    { label: "State", value: appData?.field_legalstate || "-" },
    { label: "Zip", value: appData?.field_legalzip || "-" },
    { 
      label: "Legal Phone Number", 
      value: appData?.field_businessphone ? formatPhoneNumber(appData?.field_businessphone) : "-"
    },
  ];

  return (
    <div
      className={`${isMobile ? styles.mobile_business_info : styles.business_information} ${expandAppDetails ? styles.expanded : ""}`}
    >
      <div className={isMobile ? styles.business_info_header : styles.head} onClick={toggleExpanded}>
        {isMobile ? (
          <>
            <CaretRight24 className={`${styles.caret_icon} ${expanded ? styles.expanded : ""}`} />
            <h4>Business Information</h4>
          </>
        ) : (
          <>
            <ButtonIcon>
              <CaretRight24 className={expanded ? styles.expanded : ""} />
            </ButtonIcon>
            <h5>Business information</h5>
          </>
        )}
      </div>

      <div
        className={`${isMobile ? styles.mobile_content : styles.body} ${expanded ? (isMobile ? styles.expanded : styles.body_expanded) : ""} ${expandAppDetails ? styles.app_details_expanded : ""}`}
      >
        {expanded && (
          <>
            {renderSection(null, businessInfoItems)}
            {renderSection("Contacts", contactsItems)}
            {renderSection("DBA Address", dbaAddressItems)}
            {renderSection("Legal business Address", legalAddressItems)}
          </>
        )}
      </div>
    </div>
  );
}

export default BusinessInformation;
